import { Stack, Typography } from '@mui/material';
import { DesktopDateTimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { upperFirst } from 'lodash';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import ModalTemplate from 'v2/components/Modal/ModalTemplate';
import useModalContext from 'v2/components/Modal/useModalContext';
import { getBrowser12HourTime } from 'v2/utils/dateTime';
import { formatTranslation } from 'v2/utils/formatTranslation';

interface Props {
  visitorName: string;
  groupName: string;
  initialExpirationDate: Dayjs | null;
  onSave: (expirationDate: Dayjs | null) => void;
}

interface FormValues {
  expirationDate: Dayjs | null;
}

const ExpirationDateModal: React.FC<Props> = ({ visitorName, groupName, initialExpirationDate, onSave }) => {
  const { translation } = useTranslation();
  const { closeModal } = useModalContext();
  const minExpirationDate = dayjs().add(1, 'minute');

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: { expirationDate: initialExpirationDate }
  });

  const onSubmit = handleSubmit((data) => {
    onSave(data.expirationDate);
    closeModal();
  });

  return (
    <ModalTemplate title={translation.expiration_date} primaryAction={{ label: translation.apply, onClick: onSubmit }}>
      <Stack maxWidth="396px" gap={2}>
        <Typography variant="body2">
          {formatTranslation(translation.expiration_date_modal_description, { name: visitorName, group: groupName })}
        </Typography>
        <Controller
          name="expirationDate"
          control={control}
          rules={{
            validate: (value) =>
              !value ||
              (value.isValid() && !value.isBefore(minExpirationDate)) ||
              translation.expiration_date_past_error
          }}
          render={({ field }) => (
            <DesktopDateTimePicker
              label={`${upperFirst(translation.expiration_date)} (${translation.optional.toLowerCase()})`}
              ampm={getBrowser12HourTime()}
              value={field.value}
              views={['year', 'day', 'hours', 'minutes']}
              minDateTime={minExpirationDate}
              onChange={field.onChange}
              slotProps={{
                field: { clearable: true },
                textField: {
                  id: 'expiration-date-picker',
                  error: !!errors.expirationDate,
                  helperText: errors.expirationDate?.message
                }
              }}
            />
          )}
        />
      </Stack>
    </ModalTemplate>
  );
};

export default ExpirationDateModal;
